import {useEffect, useRef, useState} from 'react';
import type {AccountModelChoices, ModelSelectionRequest} from '../src/model-catalog';
import {ErrorNotice, Loading} from './components';
import {errorDetails, request, session as authenticatedSession} from './lib/store';
import type {ErrorDetails, Resource} from './lib/types';
import './model-selector.css';

const dollars = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD',
  minimumFractionDigits: 2, maximumFractionDigits: 4});
type Scope = ModelSelectionRequest['scope'];
type Props = {accountId: string; sessionId: string | null; disabledReason: string | null; observationKey: string};

export function ModelSelector(props: Props) {
  return <ModelChoice key={`${props.accountId}:${props.sessionId ?? ''}`} {...props}/>;
}

function ModelChoice({accountId, sessionId, disabledReason, observationKey}: Props) {
  const [resource, setResource] = useState<Resource<AccountModelChoices>>({status: 'idle', data: null, error: null});
  const [scope, setScope] = useState<Scope>(sessionId === null ? 'default' : 'chat');
  const [draft, setDraft] = useState<{scope: Scope; model: string} | null>(null);
  const [saveError, setSaveError] = useState<ErrorDetails | null>(null);
  const [saving, setSaving] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const mounted = useRef(false);
  const generation = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {mounted.current = false;};
  }, []);

  useEffect(() => {
    if (saving || overrideOpen || disabledReason !== null) return;
    const current = ++generation.current;
    const controller = new AbortController();
    setResource(previous => ({status: 'loading', data: previous.data, error: null}));
    const query = sessionId === null ? '' : `?sessionId=${encodeURIComponent(sessionId)}`;
    void request<AccountModelChoices>(`/hermes/models${query}`, {signal: controller.signal}).then(data => {
      if (mounted.current && current === generation.current) setResource({status: 'ready', data, error: null});
    }, error => {
      if (!controller.signal.aborted && mounted.current && current === generation.current)
        setResource({status: 'error', data: null, error: errorDetails(error)});
    });
    return () => {controller.abort();};
  }, [accountId, sessionId, disabledReason, observationKey, saving, overrideOpen]);

  const data = resource.data;
  const selection = scope === 'chat' ? data?.chat : data?.accountDefault;
  const modelId = draft?.scope === scope ? draft.model : selection?.model ?? '';
  const model = data?.catalog.models.find(item => item.id === modelId);
  const available = data?.availability.modelIds.includes(modelId) === true;
  const editable = disabledReason === null && resource.status === 'ready' && !saving && selection !== undefined && selection !== null;
  const scopeHelp = scope === 'chat' ? 'Used for the next turns by Head of Eight and its seven roles. Other saved child models stay unchanged.'
    : 'Used by chats that have no model choice.';

  async function save(overrideConfirmed = false) {
    if (!selection || !model || !available || !editable) return;
    if (!selection.canChange && !overrideConfirmed) {setOverrideOpen(true); return;}
    const current = ++generation.current;
    setSaving(true); setSaveError(null);
    try {
      const principal = authenticatedSession();
      if (principal.accountId !== accountId) throw new Error('The signed-in account changed. Reload the model choices.');
      const body: ModelSelectionRequest = {scope, model: modelId, expectedRevision: selection.revision,
        ...(sessionId === null ? {} : {sessionId}), ...(overrideConfirmed ? {overrideConfirmed: true} : {})};
      const result = await request<AccountModelChoices>('/hermes/model', {method: 'PUT', body, csrf: principal.csrfToken});
      if (mounted.current && current === generation.current) {
        setResource({status: 'ready', data: result, error: null}); setDraft(null);
      }
    } catch (error) {
      const details = errorDetails(error);
      if (mounted.current && current === generation.current) {
        setSaveError(details);
        // Work can start after the last read. Read its native state before
        // offering the same explicit confirmation used for a known busy team.
        if (!overrideConfirmed && details.status === 409) {
          try {
            const query = sessionId === null ? '' : `?sessionId=${encodeURIComponent(sessionId)}`;
            const latest = await request<AccountModelChoices>(`/hermes/models${query}`);
            if (mounted.current && current === generation.current) {
              setResource({status: 'ready', data: latest, error: null});
              const target = scope === 'chat' ? latest.chat : latest.accountDefault;
              if (target?.canChange === false) setOverrideOpen(true);
            }
          } catch (readError) {
            if (mounted.current && current === generation.current) setSaveError(errorDetails(readError));
          }
        }
      }
    } finally {
      if (mounted.current && current === generation.current) setSaving(false);
    }
  }

  return <section className="model-selector" aria-label="Model selection">
      <Loading active={saving || resource.status === 'loading' && data === null}/>
      <div className="model-selector-fields">
        <label>Apply to<select value={scope} disabled={saving || overrideOpen || disabledReason !== null} title={scopeHelp} onChange={event => {
          setScope(event.target.value as Scope); setDraft(null); setSaveError(null);
        }}>
          {sessionId !== null && <option value="chat">This chat</option>}
          <option value="default">Account default</option>
        </select></label>
        <label>Model<select value={modelId} disabled={!editable || overrideOpen} title={selection?.blockingReason ?? undefined} onChange={event => {
          setDraft({scope, model: event.target.value}); setSaveError(null);
        }}>
          {!data?.catalog.models.some(item => item.id === modelId) && <option value={modelId} disabled>{modelId}</option>}
          {data?.catalog.models.map(item => <option key={item.id} value={item.id}
            disabled={!data.availability.modelIds.includes(item.id)}>{item.name}{data.availability.modelIds.includes(item.id) ? '' : ' · no account access'}</option>)}
        </select></label>
        <button type="button" className="button" disabled={!editable || overrideOpen || !model || !available || modelId === selection?.model}
          onClick={() => void save()}>Save</button>
      </div>
      <div className="model-selector-status">
        {data && model && <span
          title={`${data.catalog.providers.find(provider => provider.id === model.provider)?.name} standard prices, USD per ${data.catalog.tokensPerUnit.toLocaleString()} tokens. Checked ${data.catalog.updatedAt}. Context and processing rates can differ.`}>
          Input {dollars.format(model.standard.input)} · Output {dollars.format(model.standard.output)} / 1M
        </span>}
      </div>
      <ErrorNotice error={resource.error}/>
      <ErrorNotice error={saveError}/>
      {overrideOpen && model && <ModelOverrideDialog modelName={model.name} onCancel={() => setOverrideOpen(false)}
        onConfirm={() => {setOverrideOpen(false); void save(true);}}/>}
  </section>;
}

function ModelOverrideDialog({modelName, onCancel, onConfirm}: {
  modelName: string; onCancel: () => void; onConfirm: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {dialog.current?.showModal();}, []);
  return <dialog ref={dialog} className="model-override-dialog" aria-labelledby="model-override-title"
    aria-describedby="model-override-warning" onCancel={onCancel}>
    <h2 id="model-override-title">Change to {modelName}?</h2>
    <p id="model-override-warning">This stops current work and queued instructions in your account. Saved messages and files stay in place.</p>
    <div className="button-row">
      <button type="button" className="text-button" autoFocus onClick={onCancel}>Cancel</button>
      <button type="button" className="button" onClick={onConfirm}>Stop work and change model</button>
    </div>
  </dialog>;
}
